import React, { useEffect, useRef, useState, useCallback } from 'react'
import GpsDeviceTable from '../../components/Timeline/GpsDeviceTable'
import { cancelToken, dMmm, get, getQueryParams, getXlsx, isEmpty, FaSearch } from '@fareye/utils'
import {
  DEVICE_CONFIG,
  HEADER_INFLECTION,
  initialState
} from '../../config/constants'
import {
  Button,
  Card,
  Flex,
  Grid,
  Link,
  RenderControl,
  styled,
  toast,
} from '@fareye/ui'

import { 
  SearchContainer,
  SearchInput,
  CardsWrapper,
  GpsCard,
  CardTitle,
  CardValue,
  CardSubtitle,
  TableSection,
  TableHeader,
  TableTitle,
  TableActions,
  SearchButton,
  ExportButton,
  ResetButton 
} from '../../style/gpsDashboardStyle'

import GpsHistoryModal from '../../components/Timeline/GpsHistoryModal'
import { downloadToCSV } from '@fareye/common'

export function GpsDashboard() {
  const [device, setDevices] = useState(initialState)
  const [exportState, setExportState] = useState({ isLoading: false })
  const [isSearchActive, setIsSearchActive] = useState(false)
  
  const [selectedDevice, setSelectedDevice] = useState({
    data: null,
    openHistoryModal: false,
    openEditModal: false
  })

  const [meta, setMeta] = useState({
    perPage: 20,
    count: 0,
    page: 1
  })

  const [summary, setSummary] = useState({
    total: 0,
    mapped: 0,
    unmapped: 0,
    scraped: 0
  })

  type DeviceStatus = 'ALL' | 'MAPPED' | 'UNMAPPED' | 'SCRAPED'
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('ALL')

  const searchRef = useRef<HTMLInputElement>(null)
  const [reloadDevice, setReloadDevice] = useState(0)
  const [currentTotal, setCurrentTotal] = useState(0)
  const [error, setError] = useState('')
  const { devices, exportDevice, search } = DEVICE_CONFIG

  const isRequestInFlight = useRef(false);
  const fetchDevices = useCallback(async (isAppending = false, status: DeviceStatus, signal: any) => {
    if (isRequestInFlight.current) return;
    try {
      isRequestInFlight.current = true; // Lock it
      setDevices((prev) => ({ ...prev, isLoading: true }))
      
      const queryString = getQueryParams({ page_no: meta.page, status: status })
      const url = `${devices}?${queryString}`
      
      const { data: { data, meta: metaInfo, summary: summaryData } } = await get(url, signal, HEADER_INFLECTION)
      setCurrentTotal(data?.length)
      setDevices((prev) => ({
        ...prev,
        data: isAppending ? [...prev.data, ...data] : data,
        isLoading: false,
        isSuccess: true,
        isError: false
      }))
      
      setMeta(metaInfo)
      if (summaryData) setSummary(summaryData)
    } catch (ex: any) {
      if (ex?.message === 'Operation canceled by the user.') return
      setDevices((prev) => ({ ...prev, isLoading: false, isError: true }))
      console.error(ex)
    }finally {
      setTimeout(() => {
        isRequestInFlight.current = false;
      }, 100);
    }
  }, [meta.page, devices])

  useEffect(() => {
    if (isSearchActive) return

    const source = cancelToken().source()
    const isAppending = meta.page > 1
    
    fetchDevices(isAppending, deviceStatus, source.token)

    return () => source.cancel('Operation canceled by the user.')
  }, [meta.page, deviceStatus, isSearchActive, reloadDevice])

  const handleCardClick = (status: DeviceStatus) => {
    if (device.isLoading) return;
    if (status === deviceStatus && !isSearchActive) return 

    setIsSearchActive(false)
    setDeviceStatus(status)
    setMeta({
      perPage: 20,
      count: 0,
      page: 1
    })
    if (searchRef.current) searchRef.current.value = ''
    setError('')
  }

  const searchVehicle = async () => {
    const inputVal = searchRef.current?.value
    if (!inputVal) {
      toast.error('Please enter IMEI or Vehicle No')
      return
    }

    if (error) {
      toast.error('Special characters are not allowed')
      return
    }

    setIsSearchActive(true)
    const source = cancelToken().source()

    try {
      setDevices((prev) => ({ ...prev, isLoading: true }))
      let searchUrl = `${search}?search=${inputVal}`
      
      const { data: { data, meta: searchMeta } } = await get(searchUrl, source.token, HEADER_INFLECTION)

      setDevices({
        ...device,
        data,
        isLoading: false,
        isSuccess: true,
        isError: false
      })
      setMeta(searchMeta)
    } catch (ex) {
      setDevices((prev) => ({ ...prev, isLoading: false, isError: true }))
    }
  }

  const handleReset = () => {
    if (searchRef.current) searchRef.current.value = ''
    setError('')
    setIsSearchActive(false)
    setDeviceStatus('ALL')
    setMeta({ perPage: 20, count: 0, page: 1 })
  }

  const handleExport = async () => {
    const source = cancelToken().source()
    try {
      const apiUrl = `${exportDevice}?status=${deviceStatus}`
      setExportState({ isLoading: true })
      const response = await getXlsx(apiUrl, source.token)
      downloadToCSV({
        data: response.data,
        fileName: `GPSDeviceData${dMmm()}`,
        type: 'text/zip',
        extension: 'zip'
      })
      setExportState({ isLoading: false })
    } catch (ex: any) {
      setExportState({ isLoading: false })
      toast.error('Something went wrong ....')
    }
  }

  return (
    <Card>
      <SearchContainer>
        <SearchInput
          placeholder="Search IMEI or Vehicle No"
          type="text"
          ref={searchRef}
                  style={{
            border: error ? '1px solid red' : '1px solid #ccc'
          }}
          onChange={(e) => {
            const value = e.target.value

            if (/[^a-zA-Z0-9-]/.test(value)) {
              setError('Special characters are not allowed')
            } else {
              setError('')
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && searchVehicle()}
        />
        <SearchButton onClick={searchVehicle}>
          <FaSearch size={16} />
        </SearchButton>
        <ResetButton
          disabled={!searchRef.current?.value && !isSearchActive}
          onClick={handleReset}
        >
          Reset
        </ResetButton>
      </SearchContainer>

      {/* <CardsWrapper> */}
        <CardsWrapper style={{ 
            pointerEvents: device.isLoading ? 'none' : 'auto', 
            opacity: device.isLoading ? 0.7 : 1,
            transition: 'opacity 0.2s ease' 
          }}>
        {(['ALL', 'MAPPED', 'UNMAPPED', 'SCRAPED'] as const).map((status) => (
          <GpsCard 
            key={status}
            active={deviceStatus === status && !isSearchActive}
            onClick={() => handleCardClick(status)}
          >
            <CardTitle>
              {status === 'ALL' ? 'Total GPS Devices' : 
               status === 'MAPPED' ? 'Devices Mapped' : 
               status === 'UNMAPPED' ? 'Unmapped Devices' : 'Scraped Devices'}
            </CardTitle>
            <CardValue>
              {status === 'ALL' ? summary.total : 
               status === 'MAPPED' ? summary.mapped : 
               status === 'UNMAPPED' ? summary.unmapped : summary.scraped}
            </CardValue>
            <CardSubtitle>
              {status === 'ALL' ? 'Total inventory (Excl. Scraped)' : `${status.toLowerCase()} devices`}
            </CardSubtitle>
          </GpsCard>
        ))}
      </CardsWrapper>

      <TableSection>
        <TableHeader>
          <TableTitle>
            {isSearchActive ? 'Search Results' : `Total ${currentTotal} Devices`}
          </TableTitle>
          <TableActions>
            <ExportButton disabled={
              exportState.isLoading ||
              device.isLoading ||
              !device.data ||
              device.data.length === 0
            }
            onClick={handleExport}>
              {exportState.isLoading ? 'Exporting...' : 'Export Data'}
            </ExportButton>
          </TableActions>
        </TableHeader>

        {!device.isLoading && device.data?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {isSearchActive ? 'No Search Results Found' : 'No Devices Found'}
          </div>
        ) : (
                <RenderControl state={device}>
                  <GpsDeviceTable
                    setSelectedDevice={setSelectedDevice}
                    setDevices={setDevices}
                    deviceData={device}
                    meta={meta}
                    setMeta={setMeta}
                    key="device-table"
                  />
                </RenderControl>
        )}

        {selectedDevice.openHistoryModal && (
          <GpsHistoryModal
            setSelectedDevice={setSelectedDevice}
            selectedDevice={selectedDevice}
            setDevices={setDevices}
            meta={meta}
            setMeta={setMeta}
            key="history-table"
          />
        )}
      </TableSection>
    </Card>
  )
}